import React, { useMemo, useState } from 'react';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faTurnUp,
  faFilter,
  faTableList,
} from '@fortawesome/free-solid-svg-icons';
import { useGetPpDetailsQuery } from '../Dashboard/Dashboard.service';
import './ReviewDashboard.scss';
/**
 * Palli Paarvai detail panel. One endpoint (pp_details) serves six tabs via
 * view_id (1..6), scoped to the last completed month on the backend:
 *   1 class_observation      — district x designation observation %
 *   2 schools_not_observed   — district -> block -> school drill
 *   3 officials_not_observed — district x designation not-observed counts
 *   4 schools_3plus          — per school, per-designation visit counts
 *   5 low_performers         — (district, designation) with observation < 75%
 *   6 top_performers         — (district, designation) with observation >= 75%
 *
 * Views 1 & 3: matrix (DataTable) with clickable designation column headers
 * → designation drill (ReportDataTable), clickable district rows there →
 * officials drill (ReportDataTable).
 */

export interface PpBaseFilters {
  district_id?: number | string;
  block_id?: number | string;
  manage_id?: number | string;
  cate_id?: number | string;
  school_type_id?: number | string;
}

interface Props {
  baseFilters: PpBaseFilters;
  /** Current academic year id (e.g. 37 → 2026-27), resolved the same way the
   * header year dropdown resolves it. Review Dashboard tabs are frozen to the
   * current year, so this is fixed for the lifetime of the panel — not
   * user-selectable here. Sent on every pp_details call alongside month_id /
   * term_id so the backend scopes to the right year. */
  acdYr?: number | string | null;
  /** Which tab to jump to, set by the Review Dashboard card when a specific
   * KPI row (not just "View details") is clicked. `key` is bumped on every
   * click so re-clicking the same row while this panel is already open still
   * re-selects that tab (a `key`-only change still triggers the effect even
   * when `viewId` itself is unchanged). */
  openView?: { viewId: number; key: number } | null;
  /**
   * Reports the currently active tab (1-6) up to the parent, so it can react
   * to which tab is open (e.g. hiding the header's Category filter on the
   * % Class Observation / % Officials Not Observed tabs, where category
   * doesn't apply). Fired on mount and on every tab switch.
   */
  onViewChange?: (viewId: number) => void;
}

// Academic year runs June → May. Academic-month-index 1..12 maps to calendar
// month/year: 1=Jun(same year) .. 7=Dec(same year), 8=Jan(year+1) .. 12=May(year+1).
const ACADEMIC_MONTH_SEQUENCE = [6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5];

/** Academic year START calendar year for a given date (Jun–Dec → that year,
 * Jan–May → previous year). Same rule the header year dropdown uses. */
const academicStartYearFor = (d: Date) =>
  d.getMonth() >= 5 ? d.getFullYear() : d.getFullYear() - 1;

/** This date's 1-based position in the academic calendar (1=Jun .. 12=May). */
const academicIndexFor = (d: Date) => {
  const calMonth = d.getMonth() + 1; // 1-12
  const idx = ACADEMIC_MONTH_SEQUENCE.indexOf(calMonth);
  return idx + 1; // 1-based
};

// Term groupings within the academic year, by academic-month-index (1=Jun):
//   Term 1 → idx 1-4  (Jun, Jul, Aug, Sep)
//   Term 2 → idx 5-8  (Oct, Nov, Dec, Jan)
//   Term 3 → idx 9-12 (Feb, Mar, Apr, May)
const TERM_DEFS = [
  { term: 1, startIdx: 1, label: 'Term 1 (Jun - Sep)' },
  { term: 2, startIdx: 5, label: 'Term 2 (Oct - Jan)' },
  { term: 3, startIdx: 9, label: 'Term 3 (Feb - May)' },
] as const;

const PP_TABS = [
  { label: '% Class Observation', value: 1 },
  { label: '% Schools Not Observed', value: 2 },
  { label: '% Officials Not Observed', value: 3 },
  { label: '% Schools 3+ Times', value: 4 },
  { label: 'Low performers', value: 5 },
  { label: 'Top performers', value: 6 },
];

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const pct = (v: unknown) => `${num(v).toFixed(2)}%`;
const intStr = (v: unknown) => num(v).toLocaleString('en-IN');
const pctClass = (v: number) =>
  v >= 80
    ? 'pp-detail__cell pp-g'
    : v >= 60
      ? 'pp-detail__cell pp-o'
      : 'pp-detail__cell pp-r';

type Designation = {
  designation: string;
  observation_pct?: number | string;
  not_observed?: number | string;
  total_users?: number | string;
  target?: number | string;
  observed?: number | string;
  districts: Array<{
    district_id?: number | string;
    district_name?: string;
    observation_pct?: number | string;
    not_observed?: number | string;
    total_users?: number | string;
    target?: number | string;
    observed?: number | string;
    officials: Array<{
      emis_username?: number | string;
      user_name?: string;
      target?: number | string;
      observation_count?: number | string;
      observation_pct?: number | string;
    }>;
  }>;
};

// Cross-designation totals for one district (the matrix's "Overall" column)
// — sum(observed)/sum(target) across every designation, computed server-side
// in the SAME pass as the per-designation totals, so it can't drift out of
// sync with them.
type DistrictTotal = {
  district_id?: number | string;
  district_name?: string;
  observation_pct?: number | string;
  not_observed?: number | string;
  total_users?: number | string;
  target?: number | string;
  observed?: number | string;
};
// Same shape, across everything (the matrix's grand total).
type GrandTotal = DistrictTotal;

// Designations that never appear in the % Class Observation / % Officials
// Not Observed matrix (non-reviewing roles) — filtered out entirely on the
// frontend (not just defaulted off), so they don't show up as columns OR as
// filter options. Matched case-insensitively against the trimmed label.
const PP_EXCLUDED_DESIGNATIONS = new Set(
  ['Addl EO', 'EO', 'AEO', 'CSE', 'JD,SCERT', 'JD,SS'].map((d) =>
    d.toLowerCase()
  )
);

// Designation columns shown BY DEFAULT, in this exact order. Any other
// (non-excluded) designation still appears as a column option and can be
// checked in the filter, it just starts unchecked.
const PP_DEFAULT_DESIGNATION_ORDER = [
  'CEO',
  'DEO (Elementary)',
  'DEO (Secondary)',
  'BEO',
  'BRTE',
  // 'APO (Secondary)',
  // 'DC',
  // 'DIET Principal',
  // 'DIET Lecturer',
];

const PalliPaarvaiDetail: React.FC<Props> = ({
  baseFilters,
  acdYr,
  openView,
  onViewChange,
}) => {
  const [viewId, setViewId] = useState<number>(1);

  // Report the active tab up to the parent on mount and on every switch
  // (switchTab below, and the openView-driven jump) — see onViewChange's doc.
  React.useEffect(() => {
    onViewChange?.(viewId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  // view 1 / 3 client-side drill state
  const [desig, setDesig] = useState<string | null>(null); // designation header → district list
  const [cell, setCell] = useState<{
    designation: string;
    districtName: string;
  } | null>(null); // cell click → officials list

  // view 2 server-side geography drill
  const [geo, setGeo] = useState<{
    district_id?: number | string;
    district_name?: string;
    block_id?: number | string;
    block_name?: string;
  }>({});

  // Column filter for views 1 & 3 (matrix) and view 4.
  // null = use auto-default (non-zero roles only).
  const [roleSel, setRoleSel] = useState<string[] | null>(null);
  const [role3Sel, setRole3Sel] = useState<string[] | null>(null);

  // Month selection (views 1,3,4,5,6) - defaults to the current month.
  const [selectedMonth, setSelectedMonth] = useState<{
    month: number;
    year: number;
    label: string;
  } | null>(null);

  // Term selection (view 2 only) - defaults to the current term.
  const [selectedTerm, setSelectedTerm] = useState<{
    term: number;
    label: string;
  } | null>(null);

  // Generate month options spanning the CURRENT academic year (June → May),
  // capped at today — not the calendar year, which breaks Jan–May (the
  // previous "June..currentMonth" loop never ran since currentMonth < 6).
  // When today rolls into June, the academic year itself advances (handled
  // by academicStartYearFor), so the dropdown naturally restarts at the new
  // "June <nextYear>" as its only option.
  const monthOptions = useMemo(() => {
    const now = new Date();
    const startYear = academicStartYearFor(now);
    const currentIdx = academicIndexFor(now);

    return ACADEMIC_MONTH_SEQUENCE.slice(0, currentIdx).map((m, i) => {
      const year = i < 7 ? startYear : startYear + 1; // idx 1-7 = Jun-Dec, 8-12 = Jan-May
      const date = new Date(year, m - 1, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });
      return { month: m, year, label: `${monthName} ${year}` };
    });
  }, []);

  // Term options: only terms that have already started this academic year.
  const termOptions = useMemo(() => {
    const currentIdx = academicIndexFor(new Date());
    return TERM_DEFS.filter((t) => t.startIdx <= currentIdx).map((t) => ({
      term: t.term,
      label: t.label,
    }));
  }, []);

  // Default to LAST month (not the current, still-in-progress month) / current
  // term when the component mounts. monthOptions' last entry is the current
  // month, so the one before it is last month — falls back to the current
  // month if the academic year just started and there's no earlier option.
  React.useEffect(() => {
    if (selectedMonth === null && monthOptions.length > 0) {
      const idx =
        monthOptions.length > 1
          ? monthOptions.length - 2
          : monthOptions.length - 1;
      setSelectedMonth(monthOptions[idx]);
    }
  }, [monthOptions, selectedMonth]);
  React.useEffect(() => {
    if (selectedTerm === null && termOptions.length > 0) {
      setSelectedTerm(termOptions[termOptions.length - 1]);
    }
  }, [termOptions, selectedTerm]);

  const switchTab = (v: number) => {
    setViewId(v);
    setDesig(null);
    setCell(null);
    setGeo({});
    setRoleSel(null);
    setRole3Sel(null);
  };

  // The Review Dashboard card sets `openView` when a specific KPI row is
  // clicked (e.g. "% of Schools Not Observed") so this panel opens directly
  // on the matching tab instead of always defaulting to tab 1. Keyed on
  // `openView.key` (bumped on every click) rather than just `viewId`, so
  // re-clicking the same row while already open still re-selects it.
  const lastOpenKey = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!openView || openView.key === lastOpenKey.current) return;
    lastOpenKey.current = openView.key;
    switchTab(openView.viewId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openView]);

  // Helper function to render month dropdown bar
  const renderMonthDropdownBar = (
    backButton?: React.ReactNode,
    crumbContent?: React.ReactNode
  ) => (
    <div className="">
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}
      >
        {backButton}
        {crumbContent}
      </div>
    </div>
  );

  // Plain calendar month number (1-12, e.g. November → 11). Sent on views
  // 1,3,4,5,6 — combined with acd_yr the backend already has the year context,
  // so month_id itself is just the month. Resolved from the dropdown instead
  // of silently falling back to the backend's "last completed month" default.
  const monthId = selectedMonth ? selectedMonth.month : null;
  // View 2 uses a TERM dropdown instead of a month — term_id 1/2/3 groups
  // 4 months each within the current academic year.
  const termId = selectedTerm ? selectedTerm.term : null;
  const isTermView = viewId === 2;

  const args = useMemo(() => {
    const a: Record<string, unknown> = { view_id: viewId, ...baseFilters };
    if (acdYr != null) {
      a.acd_yr = acdYr;
    }
    if (isTermView) {
      if (termId != null) a.term_id = termId;
    } else if (monthId != null) {
      a.month_id = monthId;
    }
    if (viewId === 2) {
      if (geo.block_id != null) {
        a.district_id = geo.district_id ?? baseFilters.district_id;
        a.block_id = geo.block_id;
      } else if (geo.district_id != null) {
        a.district_id = geo.district_id;
        delete a.block_id;
      }
    }
    return a;
  }, [viewId, baseFilters, geo, acdYr, isTermView, monthId, termId]);

  // Wait for the current academic year AND the relevant dropdown (month for
  // views 1/3/4/5/6, term for view 2) to resolve, so the very first request
  // already carries the right scope instead of firing once without it and
  // immediately refetching.
  const { data, isFetching } = useGetPpDetailsQuery(args as never, {
    skip: acdYr == null || (isTermView ? termId == null : monthId == null),
  });
  const details = (data as { result?: { details?: unknown } } | undefined)
    ?.result?.details as
    | {
        level?: string;
        group_key?: string;
        designations?: Designation[];
        district_totals?: DistrictTotal[];
        grand_total?: GrandTotal;
        items?: Array<Record<string, unknown>>;
        roles?: string[];
        month_id?: number | string;
      }
    | undefined;

  const tabBar = (
    <div className="pp-detail__tabs">
      <SelectButton
        value={viewId}
        options={PP_TABS}
        onChange={(e) => e.value != null && switchTab(e.value)}
        allowEmpty={false}
      />
      {/* View 2 (Schools Not Observed) scopes by TERM; every other view
          scopes by MONTH. */}
      <div className="pp-detail__month-row">
        <span className="pp-detail__month-label">
          {isTermView ? 'TERM:' : 'MONTH:'}
        </span>
        {isTermView ? (
          <Dropdown
            value={selectedTerm}
            options={termOptions}
            onChange={(e) => setSelectedTerm(e.value)}
            optionLabel="label"
            placeholder="Select term"
            className="review-detail__month-dd"
          />
        ) : (
          <Dropdown
            value={selectedMonth}
            options={monthOptions}
            onChange={(e) => setSelectedMonth(e.value)}
            optionLabel="label"
            placeholder="Select month"
            className="review-detail__month-dd"
          />
        )}
      </div>
    </div>
  );

  const metric = viewId === 1 ? 'pct' : 'notobs';
  const designations = details?.designations ?? [];

  const matrix = useMemo(() => {
    if (viewId !== 1 && viewId !== 3) return null;
    type Agg = { obs: number; tgt: number; notobs: number; pct: number };
    // Reads the backend's already-computed sum(observed)/sum(target) + pct
    // for one (designation, district) pair, or a district/grand total — no
    // client-side accumulation, so the matrix can't drift from the server's
    // single source of truth for this math.
    const toAgg = (v: {
      observed?: number | string;
      target?: number | string;
      not_observed?: number | string;
      observation_pct?: number | string;
    }): Agg => ({
      obs: num(v.observed),
      tgt: num(v.target),
      notobs: num(v.not_observed),
      pct: num(v.observation_pct),
    });

    const byDistrict: Record<
      string,
      {
        district_id?: number | string;
        name: string;
        cells: Record<string, Agg>;
      }
    > = {};
    const roleSet = new Set<string>();
    const roleTotals: Record<string, Agg> = {};
    designations.forEach((d) => {
      const label = (d.designation || '').trim();
      if (!label || PP_EXCLUDED_DESIGNATIONS.has(label.toLowerCase())) return;
      roleSet.add(label);
      // Per-designation column footer — the backend's own total for this
      // designation across every district, not a re-sum of the cells below.
      roleTotals[label] = toAgg(d);
      d.districts.forEach((dist) => {
        if (
          dist.district_id == null ||
          !String(dist.district_name || '').trim()
        )
          return;
        const key = String(dist.district_id ?? dist.district_name ?? '');
        const row =
          byDistrict[key] ||
          (byDistrict[key] = {
            district_id: dist.district_id,
            name: dist.district_name || '',
            cells: {},
          });
        row.cells[label] = toAgg(dist);
      });
    });

    const rows = Object.values(byDistrict).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Wanted designations first, in the specified order; anything else
    // (non-excluded, not in the wanted list) sorted alphabetically after.
    const orderIndex = (label: string) => {
      const idx = PP_DEFAULT_DESIGNATION_ORDER.findIndex(
        (w) => w.toLowerCase() === label.toLowerCase()
      );
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    const roles = Array.from(roleSet).sort((a, b) => {
      const ia = orderIndex(a);
      const ib = orderIndex(b);
      return ia !== ib ? ia - ib : a.localeCompare(b);
    });

    // "Overall" is the SIMPLE AVERAGE of the currently selected designations'
    // own percentages (not a target-weighted sum) — computed in renderMatrix,
    // scoped to the filter, since it depends on which designations are
    // checked right now.
    return { roles, rows, roleTotals };
  }, [viewId, designations]);

  const matchingDesignations = (label: string | null) => {
    if (!label) return [] as typeof designations;
    return designations.filter((d) => (d.designation || '').trim() === label);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Views 1 & 3 — level 3: officials for a specific district+designation
  // ─────────────────────────────────────────────────────────────────────────
  const officialColumns: ReportColumn[] = [
    { key: 'user_name', label: 'USER NAME', type: 'text' },
    { key: 'designation', label: 'DESIGNATION', type: 'text' },
    { key: 'target', label: 'TARGET', type: 'number' },
    { key: 'observation_count', label: 'OBSERVED COUNT', type: 'number' },
    {
      key: 'observation_pct',
      label: 'OBSERVATION %',
      type: 'threshold',
      align: 'right',
      thresholds: { good: 80, bad: 60 },
    },
  ];

  const renderOfficials = () => {
    const officials: Array<Record<string, unknown>> = [];
    matchingDesignations(cell!.designation).forEach((d) => {
      const dist = d.districts.find(
        (x) => (x.district_name || '') === cell!.districtName
      );
      if (dist)
        dist.officials.forEach((o) =>
          officials.push({
            emis_username: String(o.emis_username ?? ''),
            user_name: o.user_name || '',
            designation: d.designation,
            target: num(o.target),
            observation_count: num(o.observation_count),
            observation_pct: pct(o.observation_pct),
          })
        );
    });
    // Default view: lowest OBSERVED COUNT first (so officials who haven't
    // observed show up immediately instead of being buried alphabetically
    // by name) — clicking the column header still toggles asc/desc as
    // usual, this only sets the order the table opens with.
    officials.sort(
      (a, b) => Number(a.observation_count) - Number(b.observation_count)
    );
    return (
      <>
        {renderMonthDropdownBar(
          <Button
            type="button"
            text
            icon="pi pi-arrow-left"
            label="All districts"
            className="review-detail__back"
            onClick={() => setCell(null)}
          />,
          <span className="review-detail__crumb">
            <FontAwesomeIcon icon={faLayerGroup} />
            <strong>
              {cell!.districtName} — {cell!.designation}
            </strong>
          </span>
        )}
        <ReportDataTable
          title={`${cell!.districtName} — ${cell!.designation}`}
          icon="pi pi-table"
          columns={officialColumns}
          data={officials}
          dataKey="emis_username"
          exportFileName="pp-officials"
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Views 1 & 3 — level 2: all districts for a clicked designation
  // ─────────────────────────────────────────────────────────────────────────
  const designationColumns: ReportColumn[] = [
    { key: 'district_name', label: 'DISTRICT', type: 'link' },
    { key: 'designation', label: 'DESIGNATION', type: 'text' },
    { key: 'users', label: 'NO. OF OFFICIALS', type: 'number' },
    { key: 'target', label: 'TARGET', type: 'number' },
    { key: 'observed', label: 'OBSERVED', type: 'number' },
    {
      key: 'observation_pct',
      label: 'OBSERVATION %',
      type: 'threshold',
      align: 'right',
      thresholds: { good: 80, bad: 60 },
    },
  ];

  const renderDesignationDrill = () => {
    const distMap: Record<
      string,
      {
        district_id?: number | string;
        district_name: string;
        users: number;
        tgt: number;
        obs: number;
      }
    > = {};
    matchingDesignations(desig).forEach((d) => {
      d.districts.forEach((dist) => {
        if (
          dist.district_id == null ||
          !String(dist.district_name || '').trim()
        )
          return;
        const key = String(dist.district_id);
        const row =
          distMap[key] ||
          (distMap[key] = {
            district_id: dist.district_id,
            district_name: dist.district_name || '',
            users: 0,
            tgt: 0,
            obs: 0,
          });
        row.users += num(dist.total_users);
        row.tgt += num(dist.target);
        row.obs += num(dist.observed);
      });
    });
    const rows = Object.values(distMap)
      .sort((a, b) => a.district_name.localeCompare(b.district_name))
      .map((r) => ({
        district_id: r.district_id,
        district_name: r.district_name,
        designation: desig || '',
        users: r.users,
        target: r.tgt,
        observed: r.obs,
        observation_pct: pct(r.tgt ? (r.obs * 100) / r.tgt : 0),
      }));
    return (
      <>
        {renderMonthDropdownBar(
          <Button
            type="button"
            text
            icon="pi pi-arrow-left"
            label="All designations"
            className="review-detail__back"
            onClick={() => setDesig(null)}
          />,
          <span className="review-detail__crumb">
            <FontAwesomeIcon icon={faLayerGroup} />
            <strong>Designation: {desig}</strong>
          </span>
        )}
        <ReportDataTable
          title={`Designation: ${desig}`}
          icon="pi pi-table"
          columns={designationColumns}
          data={rows}
          dataKey="district_id"
          exportFileName="pp-designation"
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          onRowClick={(row) =>
            setCell({
              designation: desig!,
              districtName: row.district_name as string,
            })
          }
          className="pp-officials-not-observed"
        />
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Views 1 & 3 — level 1: district × designation matrix
  // Uses DataTable/Column directly so column headers and cells can be
  // interactive (ReportDataTable doesn't support custom header/cell renderers).
  // ─────────────────────────────────────────────────────────────────────────
  const renderMatrix = () => {
    if (!matrix) return null;

    // Auto default for the filter = the curated "wanted" designations
    // (PP_DEFAULT_DESIGNATION_ORDER), in that order, when present. Any other
    // designation still shows up as a filter option but starts unchecked.
    // Falls back to showing everything if none of the wanted ones are
    // present in this data (so the table is never blank by default).
    const wantedRoles = matrix.roles.filter((role) =>
      PP_DEFAULT_DESIGNATION_ORDER.some(
        (w) => w.toLowerCase() === role.toLowerCase()
      )
    );
    const defaultRoles = wantedRoles.length > 0 ? wantedRoles : matrix.roles;

    // null = user hasn't touched the filter yet → use the auto default.
    const effectiveRoleSel = roleSel ?? defaultRoles;
    const visibleRoles = matrix.roles.filter((r) =>
      effectiveRoleSel.includes(r)
    );
    const roleOptions = matrix.roles.map((r) => ({ label: r, value: r }));
    const isFiltered =
      roleSel !== null || effectiveRoleSel.length < matrix.roles.length;

    // Per-designation column footers come straight from the backend
    // (matrix.roleTotals) — no client-side re-summing of the visible rows.
    const roleTotals = matrix.roleTotals;

    // "Overall" column/footer = the SIMPLE AVERAGE of the currently
    // SELECTED designations' own values, not a target-weighted sum — e.g.
    // with 8 designations checked and percentages 0,66.70,0,46.00,45.10,
    // 0,0,0 the overall is (0+66.70+0+46.00+45.10+0+0+0)/8 = 19.73%, not
    // sum(observed)/sum(target). For the 'notobs' metric (a raw count, not
    // a percentage) it stays a sum across the selected designations.
    const overallFor = (row: (typeof matrix.rows)[number]) => {
      if (metric === 'pct') {
        const vals = visibleRoles.map((role) => row.cells[role]?.pct ?? 0);
        const avg = vals.length
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : 0;
        return { pct: avg, notobs: 0 };
      }
      const sum = visibleRoles.reduce(
        (acc, role) => acc + (row.cells[role]?.notobs ?? 0),
        0
      );
      return { pct: 0, notobs: sum };
    };
    const grandOverall = (() => {
      if (metric === 'pct') {
        const vals = visibleRoles.map((role) => roleTotals[role]?.pct ?? 0);
        const avg = vals.length
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : 0;
        return { pct: avg, notobs: 0 };
      }
      const sum = visibleRoles.reduce(
        (acc, role) => acc + (roleTotals[role]?.notobs ?? 0),
        0
      );
      return { pct: 0, notobs: sum };
    })();

    // DISTRICT and OVERALL need to be sortable. DISTRICT already has a real
    // `name` field to sort by; OVERALL has none — it's computed on the fly
    // by overallFor() (which depends on the currently selected designations),
    // so a flat `_overallSort` value is attached to each row here, scoped by
    // the same metric the column displays, for PrimeReact's sort to read.
    const sortableRows = matrix.rows.map((r) => ({
      ...r,
      _overallSort: metric === 'pct' ? overallFor(r).pct : overallFor(r).notobs,
    }));

    return (
      <>
        {renderMonthDropdownBar()}
        <div className="review-detail__tablecard">
          <div className="review-detail__tabletop">
            <span className="review-detail__tabletitle">
              <FontAwesomeIcon icon={faTableList} />
              {metric === 'pct'
                ? '% Class Observation'
                : '% Officials Not Observed'}
              <span className="review-detail__hint">
                click a designation header to drill · click a cell for officials
              </span>
            </span>
            <div className="review-detail__tools">
              <span
                className={`review-detail__filter${isFiltered ? ' is-active' : ''}`}
              >
                <FontAwesomeIcon icon={faFilter} />
              </span>
              <MultiSelect
                value={effectiveRoleSel}
                options={roleOptions}
                onChange={(e) => setRoleSel(e.value as string[])}
                placeholder="All designations"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} items selected"
                className="review-detail__kpidd"
                panelClassName="review-detail__kpidd-panel"
                display="chip"
                scrollHeight="260px"
                showSelectAll
              />
              {roleSel !== null && effectiveRoleSel.length > 0 && (
                <Button
                  type="button"
                  outlined
                  size="small"
                  icon="pi pi-replay"
                  label="Clear"
                  className="review-detail__clearkpi"
                  onClick={() => setRoleSel(null)}
                />
              )}
            </div>
          </div>

          <DataTable
            // PrimeReact's scrollable+frozen DataTable caches column
            // widths/bodies by position, not just by Column `key` — when the
            // number of dynamic designation columns changes (filter
            // unchecked/rechecked), that cache gets out of sync and cells
            // render stale/duplicated values (e.g. showing the "Overall"
            // total under a different designation). The same thing happens
            // switching tabs (view 1 <-> view 3): the designation columns
            // are the same labels either side, so the key alone wouldn't
            // change, yet the metric (pct vs notobs) and the row data both
            // did — so viewId is included here too, forcing a full remount
            // on every tab switch instead of an in-place reconciliation.
            key={`${viewId}|${visibleRoles.join('|')}`}
            value={sortableRows}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            dataKey="name"
            className="review-detail__table pp-detail__table"
            scrollable
          >
            <Column
              header="S.No"
              body={(_r, o) => o.rowIndex + 1}
              style={{ width: 60 }}
              frozen
            />
            {/* District column — plain text, not clickable, but sortable */}
            <Column
              field="name"
              header="DISTRICT"
              sortable
              footer={`Grand Total (${matrix.rows.length})`}
              footerClassName="is-left"
              frozen
            />

            {visibleRoles.map((role) => (
              <Column
                key={role}
                header={
                  /* Clicking the header drills into that designation */
                  <button
                    type="button"
                    className="pp-detail__colhead"
                    onClick={() => setDesig(role)}
                    title={`Drill into ${role}`}
                  >
                    {role}
                  </button>
                }
                body={(r: {
                  name: string;
                  cells: Record<
                    string,
                    { obs: number; tgt: number; notobs: number; pct: number }
                  >;
                }) => {
                  const cellAgg = r.cells[role] ?? {
                    obs: 0,
                    tgt: 0,
                    notobs: 0,
                    pct: 0,
                  };
                  const v = metric === 'pct' ? cellAgg.pct : cellAgg.notobs;
                  return (
                    /* Clicking a cell drills into that district+designation.
                       Title shows the raw observed/target backing the %, so
                       a low combined "Overall" is traceable back to here
                       instead of looking like it came "out of the blue". */
                    <button
                      type="button"
                      className={`pp-detail__cell ${metric === 'pct' ? pctClass(v) : ''}`}
                      title={`${intStr(cellAgg.obs)} observed / ${intStr(cellAgg.tgt)} target`}
                      onClick={() =>
                        setCell({ designation: role, districtName: r.name })
                      }
                    >
                      {metric === 'pct' ? `${v.toFixed(2)}%` : intStr(v)}
                    </button>
                  );
                }}
                footer={(() => {
                  const t = roleTotals[role] ?? {
                    obs: 0,
                    tgt: 0,
                    notobs: 0,
                    pct: 0,
                  };
                  if (metric === 'pct') {
                    return (
                      <span className={pctClass(t.pct)}>
                        {t.pct.toFixed(2)}%
                      </span>
                    );
                  }
                  return intStr(t.notobs);
                })()}
              />
            ))}

            <Column
              field="_overallSort"
              sortable
              header={
                metric === 'pct'
                  ? 'OVERALL OBSERVATION %'
                  : 'TOTAL NOT OBSERVED'
              }
              body={(r: (typeof matrix.rows)[number]) => {
                const t = overallFor(r);
                return metric === 'pct' ? (
                  <span
                    className={pctClass(t.pct)}
                    title={`Average of ${visibleRoles.length} selected designation${visibleRoles.length === 1 ? '' : 's'}`}
                  >
                    {t.pct.toFixed(2)}%
                  </span>
                ) : (
                  intStr(t.notobs)
                );
              }}
              footer={(() => {
                if (metric === 'pct') {
                  return (
                    <span className={pctClass(grandOverall.pct)}>
                      {grandOverall.pct.toFixed(2)}%
                    </span>
                  );
                }
                return intStr(grandOverall.notobs);
              })()}
            />
          </DataTable>
        </div>
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // View 2: schools not observed (district → block → school)
  // ─────────────────────────────────────────────────────────────────────────
  const schoolNotObservedColumns: ReportColumn[] = [
    { key: 'district_name', label: 'DISTRICT', type: 'text' },
    { key: 'block_name', label: 'BLOCK', type: 'text' },
    { key: 'udise_code', label: 'UDISE CODE', type: 'text' },
    { key: 'school_name', label: 'SCHOOL NAME', type: 'text', bold: true },
    { key: 'category', label: 'CATEGORY', type: 'text' },
    { key: 'observation_count', label: 'OBSERVATION COUNT', type: 'number' },
  ];

  const renderSchoolsNotObserved = () => {
    const level = details?.level;
    const items = details?.items ?? [];
    const hasBackButton = geo.district_id != null || geo.block_id != null;
    const crumb = hasBackButton ? (
      <div className="review-detail__bar">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: 1,
          }}
        >
          <Button
            type="button"
            text
            icon="pi pi-arrow-left"
            label={
              geo.block_id != null
                ? geo.district_name || 'District'
                : 'All districts'
            }
            className="review-detail__back"
            onClick={() =>
              geo.block_id != null
                ? setGeo({
                    district_id: geo.district_id,
                    district_name: geo.district_name,
                  })
                : setGeo({})
            }
          />
        </div>
      </div>
    ) : null;

    if (level === 'school') {
      const schoolRows = items.map((it) => ({
        ...it,
        observation_count: num(it.observation_count),
      }));
      return (
        <>
          {crumb}
          <ReportDataTable
            title="Schools Not Observed"
            icon="pi pi-table"
            columns={schoolNotObservedColumns}
            data={schoolRows}
            dataKey="school_id"
            exportFileName="pp-schools-not-observed"
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </>
      );
    }

    const isDistrict = level !== 'block';
    const geoColumns: ReportColumn[] = [
      { key: 'name', label: isDistrict ? 'DISTRICT' : 'BLOCK', type: 'link' },
      {
        key: 'schools_not_observed',
        label: 'SCHOOLS NOT OBSERVED',
        type: 'number',
      },
    ];
    const geoRows = items.map((it): Record<string, unknown> => ({
      ...it,
      schools_not_observed: num(it.schools_not_observed),
    }));
    return (
      <>
        {crumb}
        <ReportDataTable
          title={isDistrict ? 'Districts' : `Blocks — ${geo.district_name}`}
          icon="pi pi-table"
          columns={geoColumns}
          data={geoRows}
          dataKey={isDistrict ? 'district_id' : 'block_id'}
          exportFileName="pp-schools-not-observed"
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          onRowClick={(row) => {
            if (isDistrict) {
              setGeo({
                district_id: row.district_id as number,
                district_name: row.name as string,
              });
            } else {
              setGeo({
                district_id: geo.district_id,
                district_name: geo.district_name,
                block_id: row.block_id as number,
                block_name: row.name as string,
              });
            }
          }}
        />
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // View 4: schools observed 3+ times (per school, per designation)
  // ─────────────────────────────────────────────────────────────────────────
  const renderSchools3plus = () => {
    const items = details?.items ?? [];
    const roles = details?.roles ?? [];

    const nonZeroRoles3 = roles.filter((role) =>
      items.some((it) => {
        const d = it.data as Record<string, unknown> | undefined;
        return num(d?.[role]) > 0;
      })
    );

    const effectiveRole3Sel = role3Sel ?? nonZeroRoles3;
    const visibleRoles = roles.filter((r) => effectiveRole3Sel.includes(r));
    const roleOptions = roles.map((r) => ({ label: r, value: r }));
    const isFiltered =
      role3Sel !== null || effectiveRole3Sel.length < roles.length;

    const baseColumns: ReportColumn[] = [
      { key: 'district_name', label: 'DISTRICT', type: 'text' },
      { key: 'block_name', label: 'BLOCK', type: 'text' },
      { key: 'udise_code', label: 'UDISE CODE', type: 'text' },
      { key: 'school_name', label: 'SCHOOL NAME', type: 'text', bold: true },
      { key: 'category', label: 'CATEGORY', type: 'text' },
      { key: 'visited_by', label: 'VISITED 3+ BY', type: 'text' },
    ];

    const columns: ReportColumn[] = [
      ...baseColumns,
      ...visibleRoles.map((role): ReportColumn => ({
        key: `role_${role}`,
        label: role,
        type: 'number',
      })),
      { key: 'total', label: 'TOTAL', type: 'number', bold: true },
    ];

    const rows = items.map((it) => {
      const row: Record<string, unknown> = {
        ...it,
        visited_by: Array.isArray(it.visited_by)
          ? (it.visited_by as string[]).join(', ')
          : '',
        total: num(it.total),
      };
      const d = it.data as Record<string, unknown> | undefined;
      roles.forEach((role) => {
        row[`role_${role}`] = num(d?.[role]);
      });
      return row;
    });

    return (
      <>
        {renderMonthDropdownBar()}
        <div className="review-detail__tablecard">
          <div className="review-detail__tabletop">
            <span className="review-detail__tabletitle">
              <FontAwesomeIcon icon={faTableList} />
              Schools Observed 3+ Times
            </span>
            {roles.length > 0 && (
              <div className="review-detail__tools">
                <span
                  className={`review-detail__filter${isFiltered ? ' is-active' : ''}`}
                >
                  <FontAwesomeIcon icon={faFilter} />
                </span>
                <MultiSelect
                  value={effectiveRole3Sel}
                  options={roleOptions}
                  onChange={(e) => setRole3Sel(e.value as string[])}
                  placeholder="All designations"
                  maxSelectedLabels={1}
                  selectedItemsLabel="{0} items selected"
                  className="review-detail__kpidd"
                  panelClassName="review-detail__kpidd-panel"
                  display="chip"
                  scrollHeight="260px"
                  showSelectAll
                />
                {role3Sel !== null && (
                  <Button
                    type="button"
                    outlined
                    size="small"
                    icon="pi pi-replay"
                    label="Clear"
                    className="review-detail__clearkpi"
                    onClick={() => setRole3Sel(null)}
                  />
                )}
              </div>
            )}
          </div>
          <ReportDataTable
            title="Schools Observed 3+ Times"
            icon="pi pi-table"
            columns={columns}
            data={rows}
            dataKey="school_id"
            exportFileName="pp-schools-3plus"
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </div>
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Views 5 & 6: low / top performers
  // ─────────────────────────────────────────────────────────────────────────
  const performerColumns: ReportColumn[] = [
    { key: 'district_name', label: 'DISTRICT', type: 'text' },
    { key: 'designation', label: 'DESIGNATION', type: 'text' },
    { key: 'target', label: 'TARGET', type: 'number' },
    { key: 'observation_count', label: 'OBSERVATION COUNT', type: 'number' },
    {
      key: 'observation_pct',
      label: 'OBSERVATION %',
      type: 'threshold',
      align: 'right',
      thresholds: { good: 80, bad: 60 },
    },
  ];

  const renderPerformers = () => {
    const items = details?.items ?? [];
    const rows = items.map((it) => ({
      ...it,
      target: num(it.target),
      observation_count: num(it.observation_count),
      observation_pct: pct(it.observation_pct),
    }));
    // Default view: Low Performers opens with the lowest OBSERVATION COUNT
    // first, Top Performers with the highest first — same "set the initial
    // order, column header click still toggles asc/desc as usual" pattern
    // as the officials table. viewId 5 = low, 6 = top.
    rows.sort((a, b) =>
      viewId === 5
        ? Number(a.observation_count) - Number(b.observation_count)
        : Number(b.observation_count) - Number(a.observation_count)
    );
    return (
      <>
        {renderMonthDropdownBar()}
        <ReportDataTable
          title={
            viewId === 5 ? 'Low Performers (< 75%)' : 'Top Performers (≥ 75%)'
          }
          icon="pi pi-table"
          columns={performerColumns}
          data={rows}
          dataKey="emis_username"
          exportFileName={
            viewId === 5 ? 'pp-low-performers' : 'pp-top-performers'
          }
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </>
    );
  };

  const renderBody = () => {
    // Mirrors the query's own skip condition: while acd_yr or the relevant
    // dropdown (month/term) hasn't resolved yet, show loading instead of an
    // empty/stale table for that one frame.
    if (
      isFetching ||
      acdYr == null ||
      (isTermView ? termId == null : monthId == null)
    )
      return <div className="pp-detail__loading">Loading…</div>;
    if (viewId === 1 || viewId === 3) {
      if (cell) return renderOfficials();
      if (desig) return renderDesignationDrill();
      return renderMatrix();
    }
    if (viewId === 2) return renderSchoolsNotObserved();
    if (viewId === 4) return renderSchools3plus();
    return renderPerformers(); // 5 & 6
  };

  return (
    <div className="pp-detail">
      {tabBar}
      <div className="pp-detail__body">{renderBody()}</div>
    </div>
  );
};

export default PalliPaarvaiDetail;
